import { Component, OnInit, Input } from '@angular/core';
import { BaseComponent } from '../../base/base.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CalendarVM, CalendarSlotVM, CounsellorPublicProfileVM, WhoAmIVM } from '../../../models';
import { CounsellorService } from '../../../services/counsellor.service';
import { SessionDuration } from '../../../models/SessionDuration';
import { CalendarSlotStatus } from '../../../models/CalendarSlotStatus';
import { AuthService } from '../../../services/auth.service';
import { SessionType } from '../../../models/SessionType';
import { SessionBookingVM } from '../../../models/SessionBookingVM';
import { AccountService } from '../../../services/account.service';
import * as moment from 'moment-timezone';
import { ProfessionalBookingCriteria } from '../../../models/ProfessionalBookingCriteria';
import { getMondayInUTC } from '../../../utils/DateUtils';

@Component({
  selector: 'pro-profile-booking',
  templateUrl: './pro-profile-booking.component.html'
})
export class ProProfileBookingComponent extends BaseComponent implements OnInit {

  @Input() profileModel: CounsellorPublicProfileVM = new CounsellorPublicProfileVM();
  calendar: CalendarVM = new CalendarVM();
  mode: BookingMode = BookingMode.isLoading;

  //Properties to track selection on calendar
  selectedSlot: CalendarSlotVM | null;
  selectedDay: number | null;
  selectedDate: Date | null;
  selectedDuration: SessionDuration | null;
  selectedSessionType: SessionType | null;

  //What the professional offers
  canDo25Mins: boolean = false;
  canDo50Mins: boolean = false;
  canDo80Mins: boolean = false;

  //What credits the client has
  has25MinsCredit: boolean = false;
  has50MinsCredit: boolean = false;
  has80MinsCredit: boolean = false;

  clientCanBook: boolean = false;
  payWithCredits: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private counsellorService: CounsellorService,
    private router: Router,
    private authService: AuthService,
    private accService: AccountService
  ) {
    super(route);
  }

  ngOnInit(): void {
    this.initBookingContext();
  }

  initBookingContext() {
    var self = this;

    switch (this.profileModel.bookingCriteria) {
      case ProfessionalBookingCriteria.inD_NO_MEMBERSHIP:
        self.clientCanBook = false;
        self.mode = BookingMode.bookingUnavailable;
        break;
      case ProfessionalBookingCriteria.inD_WITH_MEMBERSHIP:
        //IND Professional - Not B2B Opted
        self.counsellorService.getCounsellorCalendar(this.profileModel.mbNumber, getMondayInUTC())
          .subscribe(
            r => {
              self.calendar = r;                                      //update calendar
              self.profileModel.bookingCriteria = r.bookingCriteria;  //updated booking criteria
              self.clientCanBook = true;
              self.mode = BookingMode.displayCalendar;
            },
            (e: Response) => {
              switch (e.status) {
                default:
                case 404:
                  console.log(e);
                  self.router.navigateByUrl('/');
                  break;
              }
            }
          );
        break;
      case ProfessionalBookingCriteria.inD_WITH_MEMBERSHIP_B2B_OPTED:
        //IND Professional - B2B Opted Load Calendar
        self.counsellorService.getCounsellorCalendar(this.profileModel.mbNumber, getMondayInUTC())
          .subscribe(
            r => {
              self.calendar = r;                                      //update calendar
              self.profileModel.bookingCriteria = r.bookingCriteria;  //updated booking criteria
              self.clientCanBook = true;
            },
            (e: Response) => {
              console.log(e);
              self.router.navigateByUrl('/');
            }
          );

        //Also check available credits
        self.checkAvailableCredits(true);
        break;
      case ProfessionalBookingCriteria.orG_MEMBER:
        //ORG Member - check if client has credits
        self.checkAvailableCredits(false);
        break;
    }
  }

  //check to see if the client has any credits to use with this professional.
  //optionally the client can be marked as "canBookRegardless" of having any credits
  checkAvailableCredits(canBookRegardless: boolean) {
    var self = this;
    this.accService.getSessionCreditsByProfessional(this.profileModel.mbNumber)
      .subscribe(
        r => {
          r.forEach((item, i) => {
            switch (item.duration) {
              case SessionDuration.duration25Mins:
                self.has25MinsCredit = true;
                break;
              case SessionDuration.duration50Mins:
                self.has50MinsCredit = true;
                break;
              case SessionDuration.duration80Mins:
                self.has80MinsCredit = true;
                break;
            }
          })
        },
        e => {
          console.log(e);
        },
        () => {
          if (canBookRegardless) {
            //Display calendar - but also check if the client has credits to use with Professional
            let hasCredits = (self.has25MinsCredit || self.has50MinsCredit || self.has80MinsCredit)
            self.mode = BookingMode.displayCalendar;
            self.clientCanBook = true;
          }
          else {
            //client must have credits to book with this professional
            self.clientCanBook = (self.has25MinsCredit || self.has50MinsCredit || self.has80MinsCredit);
            if (self.clientCanBook) {
              //load calendar as the client has credits
              self.counsellorService.getCounsellorCalendar(this.profileModel.mbNumber, getMondayInUTC())
                .subscribe(
                  r => {
                    self.calendar = r;                                      //update calendar
                    self.profileModel.bookingCriteria = r.bookingCriteria;  //updated booking criteria
                    self.clientCanBook = true;
                    self.mode = BookingMode.displayCalendar;
                  },
                  (e: Response) => {
                    console.log(e);
                    self.router.navigateByUrl('/');
                  }
                );
            }
            else {
              //booking unavailable
              switch (self.profileModel.bookingCriteria) {
                case ProfessionalBookingCriteria.inD_NO_MEMBERSHIP:
                case ProfessionalBookingCriteria.inD_WITH_MEMBERSHIP:
                case ProfessionalBookingCriteria.inD_WITH_MEMBERSHIP_B2B_OPTED:
                case ProfessionalBookingCriteria.orG_MEMBER:
                  self.mode = BookingMode.bookingUnavailable;
                  break;
              }
            }
          }
        }
      );
  }

  nextWeek() {
    this.selectedSlot = null;
    this.selectedDay = null;
    this.selectedDate = null;

    this.emitDisplayLoadingDialog(true);

    var nextWeekDate = new Date(this.calendar.sunDate);
    nextWeekDate.setDate(nextWeekDate.getDate() + 1);
    var nextWeekDateStr = moment(this.calendar.sunDate).add(1, 'days').format('YYYY-MM-DD');

    this.counsellorService.getCounsellorCalendar(this.profileModel.mbNumber, nextWeekDate)
      .subscribe(returnValue => {
        this.calendar = returnValue;
        this.emitDisplayLoadingDialog(false);
      },
        e => {
          console.log(e);
          this.emitDisplayLoadingDialog(false);
        }
      );
  }

  previousWeek() {
    this.selectedSlot = null;
    this.selectedDay = null;
    this.selectedDate = null;

    this.emitDisplayLoadingDialog(true);

    var preWeekDate = new Date(this.calendar.monDate);
    preWeekDate.setDate(preWeekDate.getDate() - 7);
    var preWeekDateStr = moment(preWeekDate).format('YYYY-MM-DD');

    this.counsellorService.getCounsellorCalendar(this.profileModel.mbNumber, preWeekDate)
      .subscribe(returnValue => {
        this.calendar = returnValue;
        this.emitDisplayLoadingDialog(false);
      },
        e => {
          console.log(e);
          this.emitDisplayLoadingDialog(false);
        }
      );
  }

  slotClicked(dayOfWeek: number, date: Date, slot: CalendarSlotVM) {
    var self = this;

    if (slot.status == CalendarSlotStatus.available) {

      this.emitDisplayLoadingDialog(true);

      //check if user is signed in
      this.authService.validateUserToken().subscribe(
        result => {
          if (result) {
            //make selection
            slot.status = CalendarSlotStatus.selected;
            self.selectedSlot = slot;
            self.selectedDay = dayOfWeek;
            self.selectedDate = date;

            //Logic that determinse if client gets a choice to choose Payment/Credit or just the Diration Selection
            let hasCredits = (self.has25MinsCredit || self.has50MinsCredit || self.has80MinsCredit); //any credits?
            switch (self.profileModel.bookingCriteria) {
              case ProfessionalBookingCriteria.inD_NO_MEMBERSHIP:
                self.cancel();
                break;
              case ProfessionalBookingCriteria.inD_WITH_MEMBERSHIP:
                self.payWithCredits = false;
                self.displayDurationSelection();
                break;
              case ProfessionalBookingCriteria.inD_WITH_MEMBERSHIP_B2B_OPTED:
                if (hasCredits) {
                  self.mode = BookingMode.selectBookingPath
                }
                else {
                  self.payWithCredits = false;
                  self.displayDurationSelection();
                }
                break;
              case ProfessionalBookingCriteria.orG_MEMBER:
                if (hasCredits) {
                  self.payWithCredits = true;
                  self.displayDurationSelection();
                }
                else {
                  self.cancel();
                }
                break;
            }

          }
          else {
            self.mode = BookingMode.signinMode;
          }
        },
        error => {
          self.mode = BookingMode.signinMode;
          self.emitDisplayLoadingDialog(false);
        },
        () => {
          self.emitDisplayLoadingDialog(false);
        }
      );
    }
  }

  bookingPathSelected(useCredits: boolean) {
    this.payWithCredits = useCredits;
    this.displayDurationSelection();
  }

  displayDurationSelection() {
    //get handle to all slots in the same day
    var slotsByDay: CalendarSlotVM[] = new Array();
    switch (this.selectedDay) {
      case 1:
        slotsByDay = this.calendar.mon;
        break;
      case 2:
        slotsByDay = this.calendar.tue;
        break;
      case 3:
        slotsByDay = this.calendar.wed;
        break;
      case 4:
        slotsByDay = this.calendar.thu;
        break;
      case 5:
        slotsByDay = this.calendar.fri;
        break;
      case 6:
        slotsByDay = this.calendar.sat;
        break;
      case 0:
        slotsByDay = this.calendar.sun;
        break;
    }

    //PROF - PROFILE DEFAULTS
    this.canDo25Mins = this.profileModel.sessionPreferences.session25Valid;
    this.canDo50Mins = this.profileModel.sessionPreferences.session50Valid;
    this.canDo80Mins = this.profileModel.sessionPreferences.session80Valid;

    //ORG MEMBER - CREDIT ONLY
    if (this.payWithCredits && this.profileModel.bookingCriteria == ProfessionalBookingCriteria.orG_MEMBER) {
      this.canDo25Mins = this.has25MinsCredit;
      this.canDo50Mins = this.has50MinsCredit;
      this.canDo80Mins = this.has80MinsCredit;
    }

    //IND PROF - B2B OPTED USING CREDITS
    if (this.payWithCredits && this.profileModel.bookingCriteria == ProfessionalBookingCriteria.inD_WITH_MEMBERSHIP_B2B_OPTED) {
      this.canDo25Mins = (this.canDo25Mins && this.has25MinsCredit) ? true : false;
      this.canDo50Mins = (this.canDo50Mins && this.has50MinsCredit) ? true : false;
      this.canDo80Mins = (this.canDo80Mins && this.has80MinsCredit) ? true : false;
    }

    //double check there is sufficient slots to support duration
    for (var i = 0; i < slotsByDay.length; i++) {
      if (slotsByDay[i] === this.selectedSlot) {
        //check that sufficient time is available for 50mins, only if prof offers this
        if (this.canDo50Mins) {
          if (slotsByDay[i + 1] != null) {
            this.canDo50Mins = (slotsByDay[i + 1].status == CalendarSlotStatus.available);
          }
          else {
            this.canDo50Mins = false;
          }
        }

        //check that sufficient time is available for 80mins, only if prof offers this
        if (this.canDo80Mins) {
          if (slotsByDay[i + 1] != null && slotsByDay[i + 2] != null) {
            this.canDo80Mins = (slotsByDay[i + 1].status == CalendarSlotStatus.available && slotsByDay[i + 2].status == CalendarSlotStatus.available);
          }
          else {
            this.canDo80Mins = false;
          }
        }

        break;
      }
    }

    //switch to booking view
    this.mode = BookingMode.bookingDurationMode;
  }

  durationSelected(sessionDuration: SessionDuration) {
    this.selectedDuration = sessionDuration;
    this.mode = BookingMode.bookingSessionTypeMode;
  }

  proceedBookingConfirmation(sessionType: SessionType) {

    if (!this.clientCanBook) {
      this.cancel();
    }

    var sessionBooking = new SessionBookingVM();
    sessionBooking.counsellorName = this.profileModel.displayName;
    sessionBooking.date = this.selectedDate as Date;
    sessionBooking.duration = this.selectedDuration as SessionDuration;
    sessionBooking.mbNumber = this.profileModel.mbNumber;
    sessionBooking.sessionType = sessionType;
    sessionBooking.combinedStartDateTime = this.selectedSlot.start;
    sessionBooking.counsellorTZAtBooking = this.profileModel.timeZone;
    sessionBooking.professionalProfileImage = this.profileModel.profileImage;

    if (this.payWithCredits) {
      //Proceed using credits
      sessionBooking.payWithCredits = this.payWithCredits;
    }
    else {
      //Proceed to payment
      sessionBooking.payWithCredits = false;
      switch (sessionBooking.duration) {
        case SessionDuration.duration25Mins:
          sessionBooking.totalCost = this.profileModel.sessionPreferences.session25Price;
          break;
        case SessionDuration.duration50Mins:
          sessionBooking.totalCost = this.profileModel.sessionPreferences.session50Price;
          break;
        case SessionDuration.duration80Mins:
          sessionBooking.totalCost = this.profileModel.sessionPreferences.session80Price;
          break;
      }
    }

    this.emitDisplayLoadingDialog(true);
    this.accService.proceedSessionBooking(sessionBooking);
    this.router.navigateByUrl("confirm-session");
  }

  cancel() {
    //release any slot selection
    if (this.selectedSlot != null) {
      this.selectedSlot.status = CalendarSlotStatus.available;
      this.selectedSlot = null;
      this.selectedDay = null;
      this.selectedDate = null;
      this.selectedDuration = null;
    }
    this.clientCanBook = false;
    this.payWithCredits = false;
    this.has25MinsCredit = false;
    this.has50MinsCredit = false;
    this.has80MinsCredit = false;

    //reset
    this.mode = BookingMode.isLoading;
    this.initBookingContext();
  }

}

export enum BookingMode {
  bookingUnavailable = -2,
  isLoading = -1,
  displayCalendar = 0,
  selectBookingPath = 1,
  bookingDurationMode = 2,
  bookingSessionTypeMode = 3,
  signinMode = 4
}



